/**
 * AtlasReputationModule — the Reputation & Trust panel (Passport "Reputation" tab).
 *
 * "Relationships, not stats." This panel makes the local player's standing
 * legible and turns trust into an action:
 *
 *   • the four explicit server-computed scores (Trustworthiness, Creator Rating,
 *     Social Capital, Notoriety) render as read-only cards — there is NO client
 *     reducer that writes a score, so the UI can only ever mirror them;
 *   • an active Traitor tag shows as a prominent badge with its day countdown
 *     and the "−80% collaboration" consequence, attributable to the tag alone;
 *   • Caution reviews are listed in full alongside star reviews — an honest
 *     warning is first-class signal, never hidden or suppressed;
 *   • the Vouch action lets the player publicly stake non-transferable Influence
 *     on someone (`vouchFor`), with a live preview of the weight their standing
 *     lends and an explicit "at risk if they betray" warning. Outbound vouches
 *     can be revoked (`revokeVouch`) to reclaim the stake.
 *
 * Event-driven: re-renders on `atlas:reputation-updated` (a score/vouch/review/
 * betrayal row changed) and `atlas:wallet-updated` (the stake the player can
 * afford changed). The server is the authority and rejects anything invalid; the
 * UI only gates for UX (self-vouch, under-minimum/over-balance stake).
 */

import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import {
  vouchWeightX100,
  isTraitorActive,
  traitorDaysRemaining,
  collabEligibilityX100,
} from '@shared/reputation.js';
import {
  VOUCH_MIN_INF_STAKE,
  TRUST_NEUTRAL_START,
  TRUST_MAX,
  MAX_CAUTION_PENALTIES,
} from '@shared/constants.js';

/** Short identity hex for compact labels (`abcdef12…`). */
function shortHex(hex: string): string {
  return hex.length > 10 ? `${hex.slice(0, 8)}\u2026` : hex;
}

/** Current wall-clock in micros-since-epoch (matches the server Timestamp unit). */
function nowMicros(): bigint {
  return BigInt(Date.now()) * 1000n;
}

export class AtlasReputationModule implements IModule {
  readonly name = 'AtlasReputation';

  private scoresEl!: HTMLElement;
  private vouchesInEl!: HTMLElement;
  private vouchesOutEl!: HTMLElement;
  private reviewsEl!: HTMLElement;
  private target!: HTMLSelectElement;
  private stakeIn!: HTMLInputElement;
  private preview!: HTMLElement;
  private vouchBtn!: HTMLButtonElement;
  private note!: HTMLElement;

  init(_app: App): void {
    this.scoresEl = document.getElementById('rep-scores') as HTMLElement;
    this.vouchesInEl = document.getElementById('rep-vouches-in') as HTMLElement;
    this.vouchesOutEl = document.getElementById('rep-vouches-out') as HTMLElement;
    this.reviewsEl = document.getElementById('rep-reviews') as HTMLElement;
    this.target = document.getElementById('rep-vouch-target') as HTMLSelectElement;
    this.stakeIn = document.getElementById('rep-vouch-stake') as HTMLInputElement;
    this.preview = document.getElementById('rep-vouch-preview') as HTMLElement;
    this.vouchBtn = document.getElementById('rep-vouch-btn') as HTMLButtonElement;
    this.note = document.getElementById('rep-vouch-note') as HTMLElement;

    this.stakeIn.addEventListener('input', () => this.refreshPreview());
    this.target.addEventListener('change', () => this.refreshPreview());
    this.vouchBtn.addEventListener('click', () => this.vouch());

    // A new/changed score, vouch, review, or betrayal — re-render everything.
    bus.on('atlas:reputation-updated', () => this.render());
    // Our INF balance changed — affordability + preview may have flipped.
    bus.on('atlas:wallet-updated', () => this.render());
    // A remote avatar arriving/leaving changes who we can vouch for.
    bus.on('atlas:avatar-updated', () => this.populateTargets());

    this.render();
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  private render(): void {
    this.renderScores();
    this.renderVouches();
    this.renderReviews();
    this.populateTargets();
    this.refreshPreview();
  }

  /** The four explicit scores + the Traitor badge + collaboration eligibility. */
  private renderScores(): void {
    const rep = atlas.localReputation;
    this.scoresEl.innerHTML = '';

    const trust = rep?.trustworthiness ?? TRUST_NEUTRAL_START;
    const ratingX100 = rep?.creatorRatingX100 ?? 0;
    const ratingCount = rep?.ratingCount ?? 0;
    const social = rep?.socialCapital ?? 0;
    const notoriety = rep?.notoriety ?? 0;
    const traitorUntil = rep?.traitorUntil ?? 0n;

    const now = nowMicros();
    const traitor = isTraitorActive(traitorUntil, now);

    // Traitor badge — the headline social consequence, attributable to the tag
    // alone (not the numeric scores) so the "−80% collaboration" is exact.
    if (traitor) {
      const days = traitorDaysRemaining(traitorUntil, now);
      const badge = document.createElement('div');
      badge.className = 'rep-traitor';
      badge.id = 'rep-traitor-badge';
      badge.innerHTML =
        `<span class="tag">\u26a0 TRAITOR</span>` +
        `<span class="detail">${days} day${days === 1 ? '' : 's'} remaining \u00b7 ` +
        `collaboration requests reduced 80%</span>`;
      this.scoresEl.appendChild(badge);
    }

    const grid = document.createElement('div');
    grid.className = 'rep-grid';

    const ratingStr = ratingCount > 0 ? (ratingX100 / 100).toFixed(2) : '\u2014';
    grid.appendChild(this.scoreCard('Trustworthiness', `${trust}`, trust / TRUST_MAX, `${trust}/100`));
    grid.appendChild(
      this.scoreCard(
        'Creator Rating',
        ratingCount > 0 ? `${ratingStr}\u2605` : '\u2014',
        ratingCount > 0 ? ratingX100 / 100 / 5 : 0,
        `${ratingCount} review${ratingCount === 1 ? '' : 's'}`,
      ),
    );
    grid.appendChild(this.scoreCard('Social Capital', `${social}`, 0, 'inbound vouches'));
    grid.appendChild(
      this.scoreCard('Notoriety', `${notoriety}`, 0, notoriety > 0 ? 'verified flags' : 'clean'),
    );
    this.scoresEl.appendChild(grid);

    // Collaboration eligibility — what a future "collab request" check consults.
    const cautions = this.localCautionCount();
    const eligX100 = collabEligibilityX100(traitor, cautions);
    const elig = document.createElement('div');
    elig.className = 'rep-elig';
    elig.id = 'rep-elig';
    const cautionNote =
      cautions > 0
        ? ` \u00b7 ${cautions} Caution${cautions === 1 ? '' : 's'}${cautions >= MAX_CAUTION_PENALTIES ? ' (max)' : ''}`
        : '';
    elig.innerHTML =
      `<span class="label">Collaboration eligibility</span>` +
      `<span class="val">${eligX100}%${cautionNote}</span>`;
    this.scoresEl.appendChild(elig);
  }

  private scoreCard(label: string, value: string, frac: number, sub: string): HTMLElement {
    const card = document.createElement('div');
    card.className = 'rep-card';
    const pct = Math.max(0, Math.min(1, frac)) * 100;
    card.innerHTML =
      `<div class="lbl">${label}</div>` +
      `<div class="val">${value}</div>` +
      (frac > 0 ? `<div class="bar"><span style="width:${pct.toFixed(0)}%"></span></div>` : '') +
      `<div class="sub">${sub}</div>`;
    return card;
  }

  /** Active Caution reviews about the local player (compounding collab penalty). */
  private localCautionCount(): number {
    const me = atlas.identityHex;
    if (!me) return 0;
    let n = 0;
    for (const r of atlas.reviews.values()) {
      if (r.isCaution && r.subject === me) n++;
    }
    return n;
  }

  /** Outbound vouches (stake at risk, revocable) + inbound vouches (your backers). */
  private renderVouches(): void {
    const me = atlas.identityHex;
    this.vouchesOutEl.innerHTML = '';
    this.vouchesInEl.innerHTML = '';
    if (!me) return;

    const out = [...atlas.vouches.values()].filter((v) => v.voucher === me);
    const inbound = [...atlas.vouches.values()].filter((v) => v.vouchee === me && v.active);

    if (out.length === 0) {
      this.vouchesOutEl.appendChild(this.emptyRow('You have not vouched for anyone yet.'));
    } else {
      for (const v of out) this.vouchesOutEl.appendChild(this.outboundRow(v));
    }

    if (inbound.length === 0) {
      this.vouchesInEl.appendChild(this.emptyRow('No one has vouched for you yet.'));
    } else {
      for (const v of inbound) this.vouchesInEl.appendChild(this.inboundRow(v));
    }
  }

  private outboundRow(v: import('@shared/types/reputation.js').VouchEdge): HTMLElement {
    const el = document.createElement('div');
    el.className = v.active ? 'rep-vouch' : 'rep-vouch forfeited';
    const w = (v.weightX100 / 100).toFixed(2);
    const head = document.createElement('div');
    head.className = 'head';
    head.innerHTML =
      `<span class="who">${shortHex(v.vouchee)}</span>` +
      `<span class="w">${w}\u00d7</span>`;
    el.appendChild(head);
    const meta = document.createElement('div');
    meta.className = 'meta';
    meta.textContent = v.active
      ? `${v.infStaked.toString()} credits staked \u00b7 at risk if they betray`
      : `${v.infStaked.toString()} credits forfeited \u2014 they betrayed a collaborator`;
    el.appendChild(meta);
    if (v.active) {
      const actions = document.createElement('div');
      actions.className = 'actions';
      const revoke = document.createElement('button');
      revoke.textContent = `Revoke \u00b7 reclaim ${v.infStaked.toString()} credits`;
      revoke.addEventListener('click', () => this.revoke(v.vouchee));
      actions.appendChild(revoke);
      el.appendChild(actions);
    }
    return el;
  }

  private inboundRow(v: import('@shared/types/reputation.js').VouchEdge): HTMLElement {
    const el = document.createElement('div');
    el.className = 'rep-vouch';
    const w = (v.weightX100 / 100).toFixed(2);
    el.innerHTML =
      `<div class="head"><span class="who">${shortHex(v.voucher)}</span>` +
      `<span class="w">${w}\u00d7</span></div>` +
      `<div class="meta">staked ${v.infStaked.toString()} credits on your standing</div>`;
    return el;
  }

  /** Reviews about the local player — star ratings AND Caution flags, never hidden. */
  private renderReviews(): void {
    const me = atlas.identityHex;
    this.reviewsEl.innerHTML = '';
    if (!me) {
      this.reviewsEl.appendChild(this.emptyRow('No reviews yet.'));
      return;
    }
    const mine = [...atlas.reviews.values()].filter((r) => r.subject === me);
    if (mine.length === 0) {
      this.reviewsEl.appendChild(this.emptyRow('No reviews yet.'));
      return;
    }
    // Caution reviews surface first — they are the load-bearing trust signal.
    mine.sort((a, b) => (a.isCaution === b.isCaution ? 0 : a.isCaution ? -1 : 1));
    for (const r of mine) this.reviewsEl.appendChild(this.reviewRow(r));
  }

  private reviewRow(r: import('@shared/types/reputation.js').Review): HTMLElement {
    const el = document.createElement('div');
    el.className = r.isCaution ? 'rep-review caution' : 'rep-review';
    const badge = r.isCaution ? '\u26a0 Caution' : '\u2605'.repeat(r.stars) + '\u2606'.repeat(5 - r.stars);
    // badge (★ repeats) + shortHex(reviewer) are safe; r.ref is attacker-controlled review free
    // text → MUST go through textContent, never innerHTML (stored-XSS in the victim's session).
    el.innerHTML =
      `<div class="head"><span class="stars">${badge}</span>` +
      `<span class="by">${shortHex(r.reviewer)}</span></div>`;
    if (r.ref) { const ref = document.createElement('div'); ref.className = 'ref'; ref.textContent = r.ref; el.appendChild(ref); }
    return el;
  }

  private emptyRow(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'empty';
    el.textContent = text;
    return el;
  }

  // ─── Vouch action ─────────────────────────────────────────────────────────────

  /** Fill the vouch target dropdown with every other identity the client can see. */
  private populateTargets(): void {
    const me = atlas.identityHex;
    const prev = this.target.value;
    const seen = new Set<string>();
    for (const hex of atlas.remotes.keys()) if (hex !== me) seen.add(hex);
    for (const hex of atlas.reputations.keys()) if (hex !== me) seen.add(hex);
    for (const hex of atlas.compasses.keys()) if (hex !== me) seen.add(hex);

    this.target.innerHTML = '';
    if (seen.size === 0) {
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = '(no one visible to vouch for)';
      this.target.appendChild(opt);
      return;
    }
    for (const hex of seen) {
      const opt = document.createElement('option');
      opt.value = hex;
      opt.textContent = shortHex(hex);
      this.target.appendChild(opt);
    }
    if (prev && seen.has(prev)) this.target.value = prev;
  }

  /**
   * Live preview of the vouch the player would post. The WEIGHT is a function of
   * the voucher's standing (their total Influence + Trustworthiness), measured
   * before the stake locks — exactly the value the `vouchFor` reducer stamps —
   * so a low-Influence (alt) account previews a near-zero weight. The STAKE is
   * the at-risk amount; gating mirrors the server (min stake, sufficient INF).
   */
  private refreshPreview(): void {
    const stake = this.parseStake();
    const trust = atlas.localReputation?.trustworthiness ?? TRUST_NEUTRAL_START;
    const weightX100 = vouchWeightX100(atlas.walletSov, trust); // credits unification
    const weight = (weightX100 / 100).toFixed(2);
    const hasTarget = this.target.value !== '';

    let reason = '';
    if (!hasTarget) reason = 'No one visible to vouch for.';
    else if (stake === null) reason = 'Enter a whole number of Influence to stake.';
    else if (stake < VOUCH_MIN_INF_STAKE) reason = `Stake at least ${VOUCH_MIN_INF_STAKE} credits.`;
    else if (stake > atlas.walletSov) reason = `You only hold ${atlas.walletSov.toString()} credits.`;

    if (reason) {
      this.preview.className = 'rep-preview muted';
      this.preview.textContent = reason;
      this.vouchBtn.disabled = true;
    } else {
      this.preview.className = 'rep-preview';
      this.preview.innerHTML =
        `Your vouch would weigh <b>${weight}\u00d7</b>. ` +
        `<b>${stake!.toString()} INF</b> is locked and <b>forfeited</b> if they betray a collaborator.`;
      this.vouchBtn.disabled = false;
    }
  }

  /** Parse the stake field into a non-negative bigint, or null if invalid. */
  private parseStake(): bigint | null {
    const raw = this.stakeIn.value.trim();
    if (!/^\d+$/.test(raw)) return null;
    try {
      return BigInt(raw);
    } catch {
      return null;
    }
  }

  private vouch(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const hex = this.target.value;
    const stake = this.parseStake();
    // Mirror the preview gate exactly — never post an unaffordable / under-minimum
    // / self vouch even if the button was re-enabled out from under us. The server
    // is the final authority and rejects these too; this is just honest UX.
    if (!hex || stake === null || stake < VOUCH_MIN_INF_STAKE || stake > atlas.walletSov) return;
    let vouchee: Identity;
    try {
      vouchee = Identity.fromString(hex);
    } catch {
      this.setNote('Invalid identity.', 'err');
      return;
    }

    this.setNote(`Vouching for ${shortHex(hex)}\u2026`, '');
    this.vouchBtn.disabled = true;
    void conn.reducers
      .vouchFor({ vouchee, infStake: stake })
      .then(() => this.setNote(`Vouched for ${shortHex(hex)} \u00b7 ${stake.toString()} credits staked.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'))
      .finally(() => this.refreshPreview());
  }

  private revoke(hex: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    let vouchee: Identity;
    try {
      vouchee = Identity.fromString(hex);
    } catch {
      this.setNote('Invalid identity.', 'err');
      return;
    }
    this.setNote(`Revoking vouch for ${shortHex(hex)}\u2026`, '');
    void conn.reducers
      .revokeVouch({ vouchee })
      .then(() => this.setNote(`Revoked \u00b7 stake refunded.`, 'ok'))
      .catch((err: Error) => this.setNote(err.message, 'err'));
  }

  private setNote(text: string, cls: '' | 'ok' | 'err'): void {
    this.note.className = cls ? `note ${cls}` : 'note';
    this.note.textContent = text;
  }
}
